import t from 'react-translate';
import { useCallback, useContext, useEffect } from 'react';

// Redux
import { useAppDispatch } from 'redux/store';
import { setSaveStatus, updateMentoringProgram } from 'redux/actions/mentoring-programs';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { wrapThunkAction } from 'redux/utils';

// Form
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { getMomentDate } from 'shared/components/inputs/nv-datepicker';

// Components
import MatchAnnouncementDate from './match-announcement-date';
import MatchingRules from './matching-rules';

import MentoringProgramContext from '../../../context';
import moment from 'moment';
import { convertDateTime } from '../../../home/participant/session-edit-mode/session-edit-mode';
import ProgramConnections from './program-connections';

const shouldSubmitMatchAnnouncementDate = (data, matchAnnouncementDate) => {
  if (!data?.matchAnnouncementDate && matchAnnouncementDate) return true;
  // Parse via moment first: the form value can be a moment instance (or a
  // lodash-cloned moment from NvDatePicker) whose own `toString` is locale-aware
  // and unparseable by `new Date(...)` in non-English locales. NOV-103707.
  const formDateISO = moment(data.matchAnnouncementDate).toISOString();
  const savedDateISO = moment(matchAnnouncementDate).toISOString();
  return formDateISO !== savedDateISO;
}

const AssignedMatchingConfiguration = () => {
  const dispatch = useAppDispatch();
  const { mentoringProgram, timeZone } = useContext(MentoringProgramContext);
  const {
    id,
    endDate,
    releasedDate,
    autoMatchingEnabled,
    matchAnnouncementDate,
    concurrentMentorConnection,
    mentorCapacityEnabled,
  } = mentoringProgram || {};
  const translationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE
      .CONFIGURATION.ASSIGNED.MATCH_ANNOUNCEMENT_DATE;

  const validationSchema = yup.object().shape({
    maxMentees: yup.number()
          .min(1)
          .max(Infinity),
    // yup.mixed() prevents yup from coercing the NvDatePicker form value (a
    // lodash-cloned moment) to a string via `toString()`. With yup.string(),
    // the clone's locale-aware `toString` (e.g. Korean "2026.04.17 오후 9:00")
    // would be passed to the validators, making moment() unable to parse it
    // and causing validation to fail. NOV-103707.
    matchAnnouncementDate: yup.mixed().nullable()
      .test(
        'is-after-start-date',
        translationBase.ERROR_BEFORE_RELEASE_DATE(),
        (value) => {
          if (!releasedDate || !value) return true;
          const minDate = moment(releasedDate).isAfter(moment()) ? moment(releasedDate) : moment();
          const valueMoment = moment.isMoment(value) ? value : moment(value);
          return valueMoment.isAfter(minDate);
        }
      )
      .test(
        'is-before-end-date',
        translationBase.ERROR_AFTER_CLOSURE_DATE(),
        (value) => {
          if (!endDate || !value) return true;
          const valueMoment = moment.isMoment(value) ? value : moment(value);
          return valueMoment.isBefore(moment(endDate));
        }
      ),
    mentorCapacityEnabled: yup.boolean(),
    });

    const getDefaultValues = useCallback(
      () => ({
        maxMentees: concurrentMentorConnection ?? 1,
        mentorCapacityEnabled: mentorCapacityEnabled,
        matchAnnouncementDate: null,
      }),
      [mentoringProgram]
    );


  const methods = useForm({
    mode: 'all',
    defaultValues: getDefaultValues(),
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
  });

  const { handleSubmit, reset, getValues } = methods;

  useEffect(() => {
    if (mentoringProgram) {
      reset({
        maxMentees: concurrentMentorConnection ?? 1,
        mentorCapacityEnabled: mentorCapacityEnabled,
        matchAnnouncementDate: mentoringProgram?.matchAnnouncementDate
          ? getMomentDate(mentoringProgram.matchAnnouncementDate, 'L LT', timeZone.ianaTimeZone)
          : null,
      });
    }
  }, [mentoringProgram, reset]);

  const onSubmit = () => {
    const formData = getValues();
    const matchAnnouncementDateValue = formData.matchAnnouncementDate
      ? convertDateTime({
        date: formData.matchAnnouncementDate,
        timeZone: timeZone.ianaTimeZone,
        convert: false,
      }) : formData.matchAnnouncementDate;
    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
    wrapThunkAction(
      dispatch(
        updateMentoringProgram({
          id,
          autoMatchingEnabled,
          matchAnnouncementDate: matchAnnouncementDateValue,
          maxMentees: formData.maxMentees,
          mentorCapacityEnabled: formData.mentorCapacityEnabled,
        })
      )
    )
      .then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
      });
  };

  const onSubmitMatchAnnouncementDate = (data: { matchAnnouncementDate: string }) => {
    if (shouldSubmitMatchAnnouncementDate(data, matchAnnouncementDate)) {
      if (data.matchAnnouncementDate) {
        onSubmit();
      }
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <ProgramConnections onSubmit={onSubmit} />
          <MatchAnnouncementDate
            onSubmit={handleSubmit(onSubmitMatchAnnouncementDate)}
            disabled={!releasedDate}
          />
        </form>
      </FormProvider>
      <MatchingRules />
    </>
  );
};

export default AssignedMatchingConfiguration;
